import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LocalPrintshopRoundedIcon from '@material-ui/icons/LocalPrintshopRounded';
import { useDialog } from '@setvi/shared/providers/dialog';
import {
  Star,
  StarBorder,
  FolderOpen,
  CloseRounded,
  ShoppingCartOutlined
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import {
  GetResourceTypeName,
  downloadResource,
  normalizeChildResourcesToLink,
  normalizeResourceToLink
} from '@setvi/shared/utils';
import moment from 'moment';
import { ISMenuItem } from '@setvi/shared/components/sdropdown-menu/MenuItem';
import {
  AIIcon,
  AddFile,
  AddLinkIconV2,
  AddPDFIcon,
  AddPresentationIconV2,
  BigPrinterIcon,
  DeleteIconV2,
  DownloadIconV2,
  EmailIconV2,
  FedexIcon,
  InfoIconV2,
  IntelligenceIcon,
  PresentationIconV2,
  SelectIcon,
  SelectPDFPagesIcon,
  StaplesIcon,
  StarIconV2
} from '@setvi/shared/components/sicons';
import {
  DrawerContent,
  ResourceType,
  ResourceName,
  TextColor
} from '@setvi/shared/enums';

import { SAIDIalogContainer, SConfirmDialog } from '@setvi/shared/components';
import { useSubscribedMutation } from '@setvi/shared/hooks';
import { useSnackbar } from 'notistack';
import {
  ChildResource,
  ResourceCategoryClass,
  Resource as ResourceProps
} from '@setvi/shared/interfaces';
import { AIContentBox } from '@setvi/shared/components/ai/dialog-container/styled';

import { ResourcesQueryKey, deleteUserResourceMutation } from 'Services';
import { ROUTES } from 'enumsV2';
import {
  AddPresentation,
  CreateLink,
  CreatePresentation,
  Download,
  Email,
  MoreInfo
} from 'Components/Shared/Icons';
import { AskPdf } from 'componentsV2';
import type { PrintOrderCartAction } from 'componentsV2/print-order';
import { useComposeEmail } from 'providersV2/compose-email/context';
import {
  GetLink,
  MoreInfo as MoreInfoDialog,
  ResourceChildren
} from '../../Dialogs';
import { PresentationDialog as CreatePresentationDialog } from '../../../../Workspaces/MyPresentations/Components/PresentationDialog';
import { DropdownItemsType } from './useResourcesDropdownItems';
import { useFavoritesActions } from '../useFavorites';
import { AddToPresentation, LocalPrinterDialog } from '../Components';

const useStyles = makeStyles(theme => ({
  dropdownIconMarginTop: {
    marginTop: theme.spacing(1)
  },
  addPresentationIconFontSize: {
    fontSize: '1.6rem'
  },
  folderIcon: {
    height: 22
  },
  favoriteStar: {
    color: '#F08B0F'
  }
}));

// Same mark on both the dropdown item and the preview button.
const getCartIcon = (isInCart: boolean) =>
  isInCart ? (
    <CloseRounded style={{ fill: TextColor.SlateGrey }} />
  ) : (
    <ShoppingCartOutlined style={{ fill: TextColor.SlateGrey }} />
  );

const NewBadge = () => (
  <Box
    component="span"
    style={{
      background: '#1E96FC',
      color: '#fff',
      borderRadius: 12,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      whiteSpace: 'nowrap'
    }}>
    New
  </Box>
);

interface GetMenuResourceOption {
  resource: ResourceProps;
  handleSendFullFile: (resource: ResourceProps) => void;
  handleSendSelected: (
    resource: ResourceProps,
    selected: ChildResource[]
  ) => void;
  handleCloseDropdown?: () => void | null;
}

enum DropdownItemsTypes {
  pdf = 'pdf',
  email = 'email',
  moreInfo = 'moreInfo',
  download = 'download',
  viewLink = 'viewLink',
  delete = 'delete',
  ai = 'ai',
  favorites = 'favorites',
  getLink = 'getLink',
  goToCategory = 'goToCategory',
  createPresentation = 'createPresentation',
  addToPresentation = 'addToPresentation',
  print = 'print',
  printOrder = 'printOrder'
}

type ItemType = keyof typeof DropdownItemsTypes;

interface Resource extends ISMenuItem {
  itemType?: ItemType;
}

export interface ActionProps {
  type: 'Email' | 'Favorites' | 'Download' | 'Delete';
  resource?: ResourceProps;
}

export const useDropdownResource = (
  refetchLoading = false,
  onAction?: ({ type, resource }: ActionProps) => void,
  newDesign = false
) => {
  const flags = useFlags();
  const classes = useStyles();
  const { open } = useComposeEmail();
  const { isLoading, handleFavorites } = useFavoritesActions();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();
  const navigate = useNavigate();

  const { isLoading: isDeleteLoading, mutateAsync: deleteResource } =
    useSubscribedMutation(deleteUserResourceMutation(), [
      ResourcesQueryKey.USER_RESOURCES,
      ResourcesQueryKey.FAVORITES_RESOURCES
    ]);

  const onEmailResource = useCallback(
    (resource: ResourceProps) => {
      onAction?.({
        type: 'Email',
        resource
      });
      open({
        linksData: normalizeResourceToLink(resource),
        drawerContent: DrawerContent.reviewLinks
      });
    },
    [open, onAction]
  );

  const onChildResourceSelected = useCallback(
    (parentResource: ResourceProps, selected: ChildResource[]) => {
      open({
        linksData: normalizeChildResourcesToLink(selected, parentResource.Name),
        drawerContent: DrawerContent.reviewLinks
      });
      onAction?.({
        type: 'Email',
        resource: parentResource
      });
    },
    [open, onAction]
  );

  const onDownload = useCallback(
    (resource: ResourceProps) => {
      downloadResource(resource?.DownloadURLWithSas);
      onAction?.({
        type: 'Download',
        resource
      });
    },
    [onAction]
  );

  const handleMoreInfo = useCallback(
    (resource: ResourceProps) => {
      const informations = [
        {
          label: 'Resource type',
          value: GetResourceTypeName(resource?.ResourceTypeID)
        },
        {
          label: 'Last updated',
          value: `${moment
            .utc(resource?.ResourceLastChangedDate)
            .format('MM/DD/YYYY')} (${moment
            .utc(resource?.ResourceLastChangedDate)
            .local()
            .fromNow()})`
        },
        {
          label: 'Shareable',
          value: resource?.IsShareable ? 'Yes' : 'No'
        },
        ...(resource?.CategoryName
          ? [
              {
                label: 'Category name',
                value: resource?.CategoryName
              }
            ]
          : [])
      ];

      openDialog(
        <MoreInfoDialog
          informations={informations}
          thumbURL={resource?.ThumbURLWithSas || resource?.ThumbURL}
          breadcrumbs={resource?.Breadcrumbs}
          comment={resource?.Comment}
          onClose={() => closeDialog('more-info')}
        />,
        {
          id: 'more-info'
        }
      );
    },
    [openDialog, closeDialog]
  );

  const onGoToCategory = useCallback(
    (resource: ResourceProps) => {
      const link =
        resource.Breadcrumbs?.[0]?.ResourceCategoryClass ===
        ResourceCategoryClass.MY_RESOURCES
          ? ROUTES.WORKSPACES_FILES
          : ROUTES.RESOURCES;

      navigate(`${link}?categoryId=${resource?.CategoryID}`);
    },
    [navigate]
  );

  const onDeleteResource = useCallback(
    (resource: ResourceProps) => {
      openDialog(
        <SConfirmDialog
          title="Delete Resource"
          description="Are you sure you want to delete this resource?"
          type="delete"
          confirmText="Yes, Delete"
          loading={isDeleteLoading}
          onConfirm={() => {
            deleteResource({ resourceid: resource.ResourceID }).then(
              response => {
                if (response?.Errors) return;
                enqueueSnackbar('Resource has been deleted successfully.', {
                  variant: 'success'
                });
              }
            );
            onAction?.({
              type: 'Delete',
              resource
            });
            closeDialog();
          }}
          onClose={closeDialog}
        />
      );
    },
    [
      openDialog,
      closeDialog,
      isDeleteLoading,
      deleteResource,
      enqueueSnackbar,
      onAction
    ]
  );

  const getConditionalMenuOption = useCallback(
    ({
      resource,
      handleSendFullFile,
      handleSendSelected,
      handleCloseDropdown
    }: GetMenuResourceOption) => {
      const hasSubmenu =
        [ResourceType.Presentation, ResourceType.PdfDocument].includes(
          resource?.ResourceTypeID
        ) && resource.Pages > 1;

      const selectPageOption = resource.Pages > 1;

      return {
        closeOnClick: !hasSubmenu,
        ...(!hasSubmenu && {
          onClick: () => {
            handleSendFullFile(resource);
            handleCloseDropdown?.();
          }
        }),
        ...(hasSubmenu && {
          submenu: [
            {
              icon: newDesign ? (
                <AddPDFIcon />
              ) : (
                <AddFile viewBox="0 0 23 23" />
              ),
              text: `Add full ${
                resource.ResourceTypeID === ResourceType.PdfDocument
                  ? ResourceName.PdfDocument
                  : ResourceName.Presentation
              }`,
              ...(!newDesign
                ? { subText: 'Add entire file with all pages' }
                : {}),
              onClick: () => {
                handleSendFullFile(resource);
                handleCloseDropdown?.();
              }
            },
            ...(selectPageOption
              ? [
                  {
                    icon: newDesign ? (
                      <SelectPDFPagesIcon />
                    ) : (
                      <SelectIcon viewBox="0 0 23 23" />
                    ),
                    text: 'Select Pages',
                    ...(!newDesign
                      ? { subText: 'Add only relevant pages' }
                      : {}),
                    onClick: () =>
                      openDialog(
                        <ResourceChildren
                          confirm={selected =>
                            handleSendSelected(resource, selected)
                          }
                          resource={resource}
                        />
                      )
                  }
                ]
              : [])
          ]
        })
      };
    },
    [openDialog, newDesign]
  );

  const getIDs = (resource: ResourceProps) => {
    const isPPT = resource.ResourceTypeID === ResourceType.Presentation;
    return !isPPT
      ? [resource.ResourceID]
      : resource.ChildResources.map(i => i.ItemID);
  };

  const handlePrint = useCallback(
    (
      resource: ResourceProps,
      type: 'fedex' | 'staples' | 'local',
      { quantity, paperType }: { quantity: number; paperType: string }
    ) => {
      const recipients = [
        {
          Id: 0,
          CRMWhoId: '',
          CRMWhatId: '',
          ...(type === 'fedex' && {
            Email: 'printandgo@fedex.com'
          }),
          ...(type === 'staples' && {
            Email: 'staplesmobile@printme.com'
          }),
          ...(type === 'local' && {
            Email: 'localprintrequests@elanco.com'
          })
        }
      ];

      open({
        linksData: normalizeResourceToLink(resource),
        drawerContent: DrawerContent.reviewLinks,
        subject: 'print',
        emailBody: `<p><br>Please print the attached resource. Quantity: ${quantity}, Paper Type: ${paperType}</p>`,
        recipients
      });
    },
    [open]
  );

  const handlePrintDialog = useCallback(
    (resource: ResourceProps, type: 'fedex' | 'staples' | 'local') => {
      openDialog(
        <LocalPrinterDialog
          resources={[resource]}
          handleSubmit={(quantity, paperType) => {
            handlePrint(resource, type, { quantity, paperType });
            closeDialog('printer-dialog');
          }}
          onClose={() => closeDialog('printer-dialog')}
        />,
        {
          id: 'printer-dialog'
        }
      );
    },
    [openDialog, closeDialog, handlePrint]
  );

  const createDropdownMenu = useCallback(
    (
      resource: ResourceProps,
      dropdownItems: DropdownItemsType = [],
      handleCloseDropdown?: () => void | null,
      // Owned by the caller, which is a component and can run the hook.
      cartAction?: PrintOrderCartAction
    ): ISMenuItem[] => {
      const isNotDownloadable =
        !resource?.IsDownloadable || !resource?.DownloadURLWithSas;
      const isNotShareable = !resource?.IsShareable;
      const isMyResource =
        resource.ResourceClass === ResourceCategoryClass.MY_RESOURCES;
      const isPdf = resource.ResourceTypeID === ResourceType.PdfDocument;

      const allItems: Resource[] = [
        {
          itemType: DropdownItemsTypes.email,
          icon: newDesign ? <EmailIconV2 /> : <Email />,
          text: 'Email',
          ...(!newDesign && {
            subText: 'Send this resource in an email'
          }),
          disabled: isNotShareable,
          tooltipText: isNotShareable && 'This resource is not shareable',
          ...getConditionalMenuOption({
            resource,
            handleSendFullFile: onEmailResource,
            handleSendSelected: onChildResourceSelected,
            handleCloseDropdown
          })
        },
        {
          itemType: DropdownItemsTypes.favorites,
          icon: (
            <>
              {(isLoading || refetchLoading) && <CircularProgress size={20} />}
              {!isLoading && !refetchLoading && (
                <>
                  {resource.IsFavorite ? (
                    <Star
                      className={classes.favoriteStar}
                      htmlColor={TextColor.Grey}
                    />
                  ) : (
                    <>
                      {newDesign ? (
                        <StarIconV2 />
                      ) : (
                        <StarBorder htmlColor={TextColor.Grey} />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          ),
          disabled: isLoading || refetchLoading,
          text: resource.IsFavorite
            ? 'Remove from Favorites'
            : 'Add to Favorites',
          ...(!newDesign && {
            subText: resource.IsFavorite
              ? 'Remove this resource from your favorites'
              : 'Add this resource to your favorites'
          }),
          closeOnClick: false,
          onClick: () => {
            handleFavorites(resource.ResourceID, resource.IsFavorite);
            onAction?.({
              type: 'Favorites',
              resource
            });
          }
        },
        {
          itemType: DropdownItemsTypes.download,
          icon: newDesign ? <DownloadIconV2 /> : <Download />,
          text: 'Download',
          ...(!newDesign && {
            subText: 'Download this resource to your device'
          }),
          onClick: () => onDownload(resource),
          disabled: isNotDownloadable,
          tooltipText: isNotDownloadable && 'This resource is not downloadable'
        },
        ...(isPdf && flags?.pdfAssistant
          ? [
              {
                itemType: DropdownItemsTypes.ai,
                icon: newDesign ? <IntelligenceIcon /> : <AIIcon />,
                text: 'AI PDF Assistant',
                ...(!newDesign && {
                  subText: 'Engage in a smart conversation with AI'
                }),
                onClick: () =>
                  openDialog(
                    <SAIDIalogContainer
                      headerDivider={false}
                      title="AI PDF Assistant"
                      description="Ask questions about this PDF and get answers with page citations.">
                      <AIContentBox>
                        <AskPdf
                          name={resource?.Name}
                          id={String(resource?.ID)}
                          typeId={resource?.ResourceTypeID}
                          thumbnail={
                            resource?.ThumbURLWithSas || resource?.ThumbURL
                          }
                          url={
                            resource?.DownloadURLWithSas ||
                            resource?.DownloadURL
                          }
                        />
                      </AIContentBox>
                    </SAIDIalogContainer>,
                    {
                      id: 'pdf-chat',
                      preventClose: true
                    }
                  )
              }
            ]
          : []),
        {
          itemType: DropdownItemsTypes.createPresentation,
          icon: newDesign ? <PresentationIconV2 /> : <CreatePresentation />,
          text: 'Create Presentation',
          ...(!newDesign && {
            subText: 'Create a presentation with this resource'
          }),
          ...getConditionalMenuOption({
            resource,
            handleSendFullFile: () =>
              openDialog(
                <CreatePresentationDialog
                  withResources={getIDs(resource)}
                  onClose={() => closeDialog('create-presentation')}
                />,
                {
                  id: 'create-presentation'
                }
              ),
            handleSendSelected: (_, selected) =>
              openDialog(
                <CreatePresentationDialog
                  withResources={selected.map(i => i.ItemID)}
                  onClose={() => closeDialog('create-presentation')}
                />,
                {
                  id: 'create-presentation'
                }
              )
          })
        },
        {
          itemType: DropdownItemsTypes.addToPresentation,
          icon: newDesign ? <AddPresentationIconV2 /> : <AddPresentation />,
          text: 'Add to Presentation',
          ...(!newDesign && {
            subText: 'Add this resource to an existing presentation.'
          }),
          ...getConditionalMenuOption({
            resource,
            handleSendFullFile: () =>
              openDialog(
                <AddToPresentation
                  IDs={getIDs(resource)}
                  onClose={() => closeDialog('add-to-presentation')}
                />,
                {
                  id: 'add-to-presentation'
                }
              ),
            handleSendSelected: (_, selected) =>
              openDialog(
                <AddToPresentation
                  IDs={selected.map(i => i.ItemID)}
                  onClose={() => closeDialog('add-to-presentation')}
                />,
                {
                  id: 'add-to-presentation'
                }
              )
          })
        },
        ...(flags?.localPrinter
          ? [
              {
                itemType: DropdownItemsTypes.print,
                icon: <BigPrinterIcon htmlColor={TextColor.DarkGrey} />,
                text: 'Send to Local Printer',
                submenu: [
                  {
                    icon: <FedexIcon fontSize="large" />,
                    text: 'FedEx Print',
                    onClick: () => {
                      handleCloseDropdown?.();
                      handlePrintDialog(resource, 'fedex');
                    }
                  },
                  {
                    icon: <StaplesIcon fontSize="large" />,
                    text: 'Staples Print',
                    onClick: () => {
                      handleCloseDropdown?.();
                      handlePrintDialog(resource, 'staples');
                    }
                  },
                  {
                    icon: <LocalPrintshopRoundedIcon />,
                    text: 'Local Print',
                    onClick: () => {
                      handleCloseDropdown?.();
                      handlePrintDialog(resource, 'local');
                    }
                  }
                ]
              }
            ]
          : []),
        ...(cartAction?.enabled
          ? [
              {
                itemType: DropdownItemsTypes.printOrder,
                icon: cartAction.loading ? (
                  <CircularProgress size={20} />
                ) : (
                  getCartIcon(cartAction.isInCart)
                ),
                text: cartAction.label,
                ...(cartAction.isInCart ? {} : { badge: <NewBadge /> }),
                disabled: cartAction.disabled,
                tooltipText: cartAction.tooltip,
                // The menu stays open while the mutation runs (showing the
                // spinner) and closes automatically once it settles.
                closeOnClick: true,
                onClick: cartAction.toggle
              }
            ]
          : []),
        {
          itemType: DropdownItemsTypes.getLink,
          icon: newDesign ? <AddLinkIconV2 /> : <CreateLink fontSize="small" />,
          text: 'Get link',
          ...(!newDesign && { subText: 'Copy a link to paste anywhere' }),
          onClick: () =>
            openDialog(
              <GetLink
                resourceId={resource.ID}
                onClose={() => closeDialog('get-link')}
              />,
              {
                id: 'get-link'
              }
            ),
          disabled: isNotShareable,
          tooltipText: isNotShareable && 'This resource is not shareable'
        },
        {
          itemType: DropdownItemsTypes.goToCategory,
          icon: <FolderOpen />,
          text: 'Go to Category',
          ...(!newDesign && { subText: 'Jump into the main category' }),
          onClick: () => onGoToCategory(resource)
        },
        isMyResource && {
          itemType: DropdownItemsTypes.delete,
          icon: <DeleteIconV2 />,
          text: 'Delete',
          ...(!newDesign && { subText: 'Delete this resource' }),
          onClick: () => onDeleteResource(resource),
          color: 'danger'
        },
        {
          itemType: DropdownItemsTypes.moreInfo,
          icon: newDesign ? <InfoIconV2 /> : <MoreInfo />,
          text: 'More Info',
          ...(!newDesign && { subText: 'More details about this resource' }),
          onClick: () => handleMoreInfo(resource)
        }
      ];

      if (dropdownItems.length) {
        return allItems.filter(dropdownItem =>
          dropdownItems.includes(dropdownItem.itemType)
        );
      }

      return allItems;
    },
    [
      newDesign,
      getConditionalMenuOption,
      onEmailResource,
      onChildResourceSelected,
      isLoading,
      refetchLoading,
      classes.favoriteStar,
      flags?.pdfAssistant,
      flags?.localPrinter,
      handleFavorites,
      onAction,
      onDownload,
      openDialog,
      closeDialog,
      handlePrintDialog,
      onGoToCategory,
      onDeleteResource,
      handleMoreInfo
    ]
  );

  return {
    createDropdownMenu
  };
};
